import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import {
  Col,
  Form,
  Row,
  Select,
  Divider,
} from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Buckets } from 'ontraccr-common';
import { PlusOutlined } from '@ant-design/icons';

import getGoogleMapKey from '../common/keys';
import OnTraccrMap from '../common/map/OnTraccrMap';
import OnTraccrTextInput from '../common/inputs/OnTraccrTextInput';
import DisplayText from '../common/text/DisplayText';
import { bucketLinkTypeToDisplayName, getUpstreamName } from './bucketHelpers';
import { getIdMap } from '../helpers/helpers';
import { getBucketCustomData } from './state/buckets.actions';
import CustomFields from '../common/customFields/CustomFields';
import BucketOverrideNameFormItem from './BucketOverrideNameFormItem';
import BucketOverrideLabelFormItem from './BucketOverrideLabelFormItem';
import { createStatus } from '../statuses/state/statuses.actions';
import SimpleTextInputModal from '../common/modals/SimpleTextInputModal';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import useToggle from '../common/hooks/useToggle';

export default function BucketDetailView({
  id,
  templateId,
  name,
  number,
  description,
  overrideName,
  overrideLabel,
  upstreamTypes,
  upstreamIdMap,
  downstreamIds,
  isDisplay,
  isAdd,
  formRef,
  projectMap,
  bucketTemplateMap,
  errors,
  customFields,
  hasEquipmentDownstream,
  statusId,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const customers = useSelector((state) => state.customers.customers);
  const buckets = useSelector((state) => state.buckets.buckets);
  const customData = useSelector((state) => state.buckets.customData);
  const customDataFiles = useSelector((state) => state.buckets.customDataFiles);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const statuses = useSelector((state) => state.statuses.statuses);

  const [shouldUpdate, forceUpdate] = useReducer((x) => x + 1, 0);
  const [statusOptions, setStatusOptions] = useState([]);
  const [initialStatusOptions, setInitialStatusOptions] = useState({});

  const {
    toggle: toggleAddStatusModal,
    isToggled: isAddStatusModalVisible,
  } = useToggle();

  const bucketMap = useMemo(() => getIdMap(buckets), [buckets]);
  const markers = useMemo(() => downstreamIds?.map((id) => {
    const project = projectMap[id];
    if (!project) return null;

    return {
      lat: project?.latitude,
      lng: project?.longitude,
      title: project?.name,
      address: project?.address,
      id: project?.id,
    };
  }).filter(Boolean), [downstreamIds, projectMap]);

  const relevantStatuses = statuses[templateId];
  if (relevantStatuses && initialStatusOptions !== relevantStatuses) {
    setInitialStatusOptions(relevantStatuses);
    setStatusOptions(
      Object.values(relevantStatuses).map((status) => ({
        value: status.id,
        label: status.status,
      })),
    );
  }

  const selectedStatus = statusOptions.find((option) => option.value === statusId);

  const onAddStatus = (status) => {
    toggleAddStatusModal();
    CustomConfirmModal({
      title: 'Add Status',
      content: 'Are you sure you want to add this status? Confirming will create a new status in the system.',
      onOk: async () => {
        const newStatus = await dispatch(createStatus(templateId, {
          status,
        }));

        if (newStatus) {
          formRef.current.setFieldsValue({
            statusId: newStatus.id,
          });
        }
      },
      zIndex: 1100,
    })
  };

  useEffect(() => {
    dispatch(getBucketCustomData(id));
  }, [id]);

  useEffect(() => {
    forceUpdate();
  }, [customData]);

  const customFieldDivisions = useMemo(() => Array.from(selectedDivisions), [selectedDivisions]);

  const getUpstreamSource = useCallback((upstreamType) => {
    switch (upstreamType) {
      case 'customer-one':
      case 'customer-many':
        return Object.values(customers).map((customer) => ({
          value: customer.id,
          label: customer.name,
        }));
      default: {
        const bucketTemplateId = Buckets.getTemplateIdFromType(upstreamType, null);
        if (!bucketTemplateId) return [];
        return buckets.filter(
          (bucket) => bucket.bucketTemplateId === bucketTemplateId,
        ).map((bucket) => ({
          value: bucket.id,
          label: bucket.name,
        }));
      }
    }
  }, [buckets, customers]);

  const dropdownRender = (menu) => (
    <div style={{ width: '100%', padding: 5 }}>
    {menu}
      <Divider style={{ margin: '4px 0px 8px 0px' }} />
      <Row justify="end">
        <OnTraccrButton
          icon={ <PlusOutlined />}
          onClick={toggleAddStatusModal}
          title="Add Status"
        />
      </Row>
    </div>
  );

  return (
    <Row style={{ width: '100%', height: '100%' }} gutter={20}>
      <Col span={isAdd || !markers?.length ? 24 : 14}>
        <Form
          layout="vertical"
          ref={formRef}
          initialValues={{
            name,
            description,
            overrideName,
            overrideLabel,
            statusId,
            ...upstreamIdMap,
          }}
        >
          {!!bucketTemplateMap[templateId]?.showAutoNumber && number != null && (
            <Form.Item
              label="Number"
              style={{ marginBottom: 20 }}
            >
              <DisplayText title={number} />
            </Form.Item>
          )}
          <Form.Item
            label="Name"
            name="name"
            style={{ marginBottom: 20 }}
            rules={[{ required: true, message: 'Please input a name' }]}
          >
            { !isDisplay ? (
              <OnTraccrTextInput style={{ width: '100%' }} />
            ) : <DisplayText title={name} />}
          </Form.Item>
          <Form.Item
            label="Description"
            name="description"
            style={{ marginBottom: 20 }}
          >
            { !isDisplay ? (
              <OnTraccrTextInput style={{ width: '100%' }} />
            ) : <DisplayText title={description} />}
          </Form.Item>
          { !!upstreamTypes?.length && upstreamTypes.map((upstreamType) => (
            <Form.Item
              key={upstreamType}
              label={bucketLinkTypeToDisplayName(t, upstreamType, bucketTemplateMap)}
              name={upstreamType}
              style={{ marginBottom: 20 }}
            >
              { !isDisplay ? (
                <Select
                  options={getUpstreamSource(upstreamType)}
                  mode={upstreamType.includes('many') ? 'multiple' : null}
                  style={{ width: '100%' }}
                  optionFilterProp="label"
                  showSearch
                />
              ) : (
                <DisplayText
                  title={
                    getUpstreamName({
                      upstreamType,
                      upstreamIds: upstreamIdMap[upstreamType] ?? [],
                      customers,
                      bucketMap,
                      bucketTemplateMap,
                      t,
                    })
                  }
                />
              )}
            </Form.Item>
          ))}
          <Form.Item
            name="statusId"
            label="Status"
            style={{ marginBottom: 20 }}
          >
            { isDisplay
              ? <DisplayText title={selectedStatus?.label} />
              : <Select options={statusOptions} dropdownRender={dropdownRender} />
            }
          </Form.Item>
          { !!hasEquipmentDownstream && (
            <Form.Item
              name="overrideName"
              style={{ marginBottom: 20 }}
            >
              <BucketOverrideNameFormItem isDisplay={isDisplay} />
            </Form.Item>
          )}
          { !!hasEquipmentDownstream && (
            <Form.Item
              name="overrideLabel"
              style={{ marginBottom: 20 }}
            >
              <BucketOverrideLabelFormItem isDisplay={isDisplay} type="equipment" />
            </Form.Item>
          )}
          {!!customFields?.length && (
            <Form.Item
              name="customData"
              style={{ marginTop: 10 }}
            >
              <CustomFields
                key={`${id}${shouldUpdate}`} // Need to force re-render
                initialValues={customData}
                customFields={customFields}
                divisions={customFieldDivisions}
                errors={errors}
                fileMap={customDataFiles}
                isDisplay={isDisplay}
              />
            </Form.Item>
          )}
        </Form>
      </Col>
      {!isAdd && !!markers?.length && (
        <Col span={10}>
          <OnTraccrMap
            isNotDisplay={false}
            googleMapURL={getGoogleMapKey()}
            loadingElement={<div style={{ height: '100%' }} />}
            containerElement={(
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  height: 250,
                }}
              />
            )}
            markers={markers}
            mapElement={<div style={{ height: '100%' }} />}
            showPin={false}
          />
        </Col>
      )}
      <SimpleTextInputModal
        title="Add Status"
        visible={isAddStatusModalVisible}
        onClose={toggleAddStatusModal}
        placeholder="Enter Status"
        onSave={onAddStatus}
      />
    </Row>
  );
}

BucketDetailView.propTypes = {
  id: PropTypes.string,
  templateId: PropTypes.string,
  name: PropTypes.string,
  number: PropTypes.number,
  description: PropTypes.string,
  upstreamTypes: PropTypes.arrayOf(PropTypes.string),
  upstreamIdMap: PropTypes.shape({ [PropTypes.string]: PropTypes.arrayOf(PropTypes.string) }),
  downstreamIds: PropTypes.arrayOf(PropTypes.string),
  isDisplay: PropTypes.bool,
  isAdd: PropTypes.bool,
  formRef: PropTypes.shape({}),
  projectMap: PropTypes.shape({}),
  bucketTemplateMap: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
  errors: PropTypes.shape({}).isRequired,
  customFields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hasEquipmentDownstream: PropTypes.bool,
  statusId: PropTypes.string,
};

BucketDetailView.defaultProps = {
  id: null,
  templateId: null,
  name: null,
  number: null,
  description: null,
  upstreamTypes: [],
  upstreamIdMap: {},
  downstreamIds: [],
  isDisplay: false,
  isAdd: false,
  formRef: {},
  projectMap: {},
  hasEquipmentDownstream: false,
  statusId: null,
  bucketTemplateMap: {},
};
